import type { LayerState, LayerStatus } from '@vitals/shared';
import type { ClientStore } from '../state/clientStore.js';
import { MAP_TOGGLES, type Visibility } from './visibility.js';

/** Toggles with no realtime layer status: static route geometry + device-local. */
const NON_REALTIME_TOGGLES: ReadonlySet<string> = new Set([
  'subwaymap',
  'lirrmap',
  'mnrmap',
  'pathmap',
  'location',
]);

/** Realtime map toggles — toggle id === layer id for these by convention. */
const REALTIME_IDS: ReadonlySet<string> = new Set(
  MAP_TOGGLES.filter((t) => !NON_REALTIME_TOGGLES.has(t.id)).map((t) => t.id),
);
const LABELS: ReadonlyMap<string, string> = new Map(MAP_TOGGLES.map((t) => [t.id, t.label]));

export interface DegradedFeed {
  layerId: string;
  label: string;
  status: 'stale' | 'down';
  /** Epoch ms the problem dates from: last-good data time when `stale`
   * (the stale state re-serves last-good, so generatedAt IS that time),
   * first client observation when `down`. */
  sinceMs: number;
}

/**
 * Watches the ClientStore for realtime map layers whose feed status is not
 * `ok`, so the HUD can show an honest "feed down" indicator instead of a
 * confidently empty map (2026-07-18 LIRR frozen-feed incident).
 */
export class FeedHealth {
  private firstDegradedAt = new Map<string, number>();
  private lastStatus = new Map<string, LayerStatus>();
  private listeners = new Set<() => void>();
  private readonly offStore: () => void;

  constructor(
    private store: ClientStore,
    private now: () => number = Date.now,
  ) {
    this.offStore = store.onChange((layerId) => this.track(layerId));
  }

  private track(layerId: string): void {
    if (!REALTIME_IDS.has(layerId)) return;
    const state = this.store.get(layerId);
    if (!state) return;
    if (state.status === 'ok') {
      this.firstDegradedAt.delete(layerId);
    } else if (!this.firstDegradedAt.has(layerId)) {
      this.firstDegradedAt.set(layerId, this.now());
    }
    const prev = this.lastStatus.get(layerId);
    this.lastStatus.set(layerId, state.status);
    if (prev !== state.status) for (const cb of this.listeners) cb();
  }

  private toDegraded(state: LayerState): DegradedFeed {
    const status = state.status as 'stale' | 'down';
    return {
      layerId: state.layerId,
      label: LABELS.get(state.layerId) ?? state.layerId,
      status,
      sinceMs:
        status === 'stale'
          ? state.generatedAt
          : (this.firstDegradedAt.get(state.layerId) ?? state.generatedAt),
    };
  }

  getDegraded(): DegradedFeed[] {
    const out: DegradedFeed[] = [];
    for (const id of REALTIME_IDS) {
      const state = this.store.get(id);
      if (state && state.status !== 'ok') out.push(this.toDegraded(state));
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }

  getDegradedVisible(vis: Visibility): DegradedFeed[] {
    return this.getDegraded().filter((f) => vis.isVisible(f.layerId));
  }

  isDegraded(layerId: string): boolean {
    const state = this.store.get(layerId);
    return REALTIME_IDS.has(layerId) && state !== undefined && state.status !== 'ok';
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  destroy(): void {
    this.offStore();
    this.listeners.clear();
  }
}
